import { useParams, useNavigate } from 'react-router-dom';
import { useProdutoPorSlug } from '@/hooks/useProdutos';
import { useCarrinhoStore } from '@/stores/carrinhoStore';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ArrowLeft, ShoppingCart, Package, Check } from 'lucide-react';
import { formatCurrency, extrairVideoId } from '@/lib/utils';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { ReviewForm } from '@/components/ReviewForm';
import { Star, Heart } from 'lucide-react';
import { useWishlist } from '@/hooks/useWishlist';

export function ProdutoDetalhe() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: produto, isLoading } = useProdutoPorSlug(slug!);
  const adicionarItem = useCarrinhoStore((state) => state.adicionarItem);
  const [quantidade, setQuantidade] = useState(1);
  const [adicionado, setAdicionado] = useState(false);

  // Reviews data
  const { data: reviews, isLoading: loadingReviews, refetch: refetchReviews } = useQuery({
    queryKey: ['reviews', produto?.id],
    queryFn: async () => {
      if (!produto?.id) return [];
      const res = await api.get(`/reviews/produto/${produto.id}`);
      return res.data;
    },
    enabled: !!produto?.id
  });

  const { isFavorited, toggleWishlist } = useWishlist();
  const favorited = produto?.id ? isFavorited(produto.id) : false;

  const handleAdicionar = () => {
    if (produto) {
      adicionarItem(produto, quantidade);
      setAdicionado(true);
      setTimeout(() => setAdicionado(false), 2000);
    }
  };

  if (isLoading) {
    return (
      <div className="py-12 text-center text-gray-500">
        Carregando...
      </div>
    );
  }

  if (!produto) {
    return (
      <div className="py-12 text-center">
        <Package className="mx-auto h-12 w-12 text-gray-300 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900">Produto não encontrado</h1>
      </div>
    );
  }

  const videoId = produto.videoTimelapseId || (produto.videoTimelapseUrl && extrairVideoId(produto.videoTimelapseUrl));

  return (
    <div className="py-12">
      <div className="mx-auto max-w-7xl px-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Imagens */}
          <div className="space-y-4">
            <div className="aspect-square rounded-xl border border-fort3d-cinza-medio bg-fort3d-cinza-escuro flex items-center justify-center overflow-hidden">
              {produto.fotos?.[0] ? (
                <img
                  src={produto.fotos[0]}
                  alt={produto.nome}
                  className="h-full w-full object-cover"
                />
              ) : (
                <Package className="h-24 w-24 text-fort3d-cinza-claro" />
              )}
            </div>
            
            {/* Video Timelapse */}
            {videoId && (
              <div className="aspect-video rounded-xl overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${videoId}`}
                  title="Timelapse da Impressão"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="border-0"
                />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-6">
            <div className="flex justify-between items-start border-b border-gray-100 pb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{produto.nome}</h1>
                <p className="text-gray-500">SKU: {produto.skuInterno}</p>
              </div>
              <button
                onClick={() => toggleWishlist(produto.id)}
                className={`p-3 rounded-full transition-all duration-300 ${
                  favorited ? 'bg-rose-500 text-white shadow-lg' : 'bg-gray-100 text-gray-500 hover:bg-rose-100 hover:text-rose-500'
                }`}
              >
                <Heart className={`w-6 h-6 ${favorited ? 'fill-current' : ''}`} />
              </button>
            </div>

            <div className="text-4xl font-bold text-fort3d-laranja">
              {formatCurrency(produto.precoVenda)}
            </div>

            {produto.estoqueDisponivel === 0 ? (
              <Badge variant="error" className="text-base px-4 py-2">Esgotado</Badge>
            ) : produto.estoqueDisponivel < 5 ? (
              <Badge variant="warning" className="text-base px-4 py-2">
                Apenas {produto.estoqueDisponivel} unidades
              </Badge>
            ) : (
              <Badge variant="success" className="text-base px-4 py-2">Em estoque</Badge>
            )}

            <div className="prose max-w-none">
              <p className="text-gray-600 leading-relaxed">
                {produto.descricao}
              </p>
            </div>

            {/* Quantidade */}
            {produto.estoqueDisponivel > 0 && (
              <div className="flex items-center gap-4">
                <span className="text-gray-700">Quantidade:</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setQuantidade(Math.max(1, quantidade - 1))}
                    className="h-10 w-10 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    -
                  </button>
                  <span className="w-12 text-center text-gray-900 font-medium">{quantidade}</span>
                  <button
                    onClick={() => setQuantidade(Math.min(produto.estoqueDisponivel, quantidade + 1))}
                    className="h-10 w-10 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>
            )}

            {/* Botão */}
            {produto.estoqueDisponivel > 0 && (
              <Button
                variant="nuvem"
                size="xl"
                className="w-full sm:w-auto"
                onClick={handleAdicionar}
                leftIcon={adicionado ? <Check className="h-5 w-5" /> : <ShoppingCart className="h-5 w-5" />}
              >
                {adicionado ? 'Adicionado!' : 'Adicionar ao Carrinho'}
              </Button>
            )}

            {/* Especificações */}
            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-6 space-y-4">
              <h3 className="font-semibold text-gray-900">Especificações Técnicas</h3>
              <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm">
                <div>
                  <span className="text-gray-500 block text-xs uppercase tracking-wider mb-1">Material</span>
                  <p className="text-gray-900 font-medium">
                    {produto.filamento ? `${produto.filamento.material} ${produto.filamento.cor}` : 'Filamento 3D'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 block text-xs uppercase tracking-wider mb-1">Impressão (aprox.)</span>
                  <p className="text-gray-900 font-medium">
                    {produto.tempoImpressaoMinutos 
                      ? `${Math.max(1, Math.round(produto.tempoImpressaoMinutos / (produto.quantidadeLote || 1)))} min` 
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 block text-xs uppercase tracking-wider mb-1">Peso</span>
                  <p className="text-gray-900 font-medium">
                    {produto.gramasFilamentoProduto 
                      ? `${(Number(produto.gramasFilamentoProduto) / (produto.quantidadeLote || 1)).toFixed(0)}g` 
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 block text-xs uppercase tracking-wider mb-1">Dimensões (aprox.)</span>
                  <p className="text-gray-900 font-medium">
                    {produto.largura && produto.altura && produto.comprimento 
                      ? `${produto.largura} x ${produto.altura} x ${produto.comprimento} cm` 
                      : 'Sob consulta'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-20 border-t border-gray-100 pt-12">
          <div className="grid lg:grid-cols-3 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Avaliações de Clientes</h2>
              <div className="flex items-center gap-4 mb-8">
                <div className="text-5xl font-black text-gray-900">
                  {reviews && reviews.length > 0 
                    ? (reviews.reduce((acc: number, r: any) => acc + r.nota, 0) / reviews.length).toFixed(1)
                    : '0.0'}
                </div>
                <div>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        className={`w-4 h-4 ${
                          (reviews?.reduce((acc: number, r: any) => acc + r.nota, 0) / reviews?.length) >= star 
                          ? 'fill-yellow-400 text-yellow-400' 
                          : 'text-gray-300'
                        }`} 
                      />
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">baseado em {reviews?.length || 0} avaliações</p>
                </div>
              </div>

              {/* Formulário de Review (Opcional: Aparecer apenas para quem comprou) */}
              <ReviewForm produtoId={produto.id} pedidoId="MODAL_OU_AUTO" onSuccess={() => refetchReviews()} />
            </div>

            <div className="lg:col-span-2 space-y-8">
              {loadingReviews ? (
                <div className="space-y-4 animate-pulse">
                  {[1, 2, 3].map(i => <div key={i} className="h-32 bg-gray-50 rounded-xl" />)}
                </div>
              ) : reviews?.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl">
                  <p className="text-gray-500">Ainda não há avaliações para este produto.</p>
                  <p className="text-sm text-gray-400 mt-1">Seja o primeiro a avaliar!</p>
                </div>
              ) : (
                reviews?.map((review: any) => (
                  <div key={review.id} className="pb-8 border-b border-gray-100 last:border-0">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star 
                            key={star} 
                            className={`w-3.5 h-3.5 ${review.nota >= star ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                          />
                        ))}
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(review.createdAt).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <h4 className="font-bold text-gray-900 mb-1">{review.titulo}</h4>
                    <p className="text-sm text-gray-600 mb-3">{review.comentario}</p>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500">
                        {review.cliente.nome.charAt(0)}
                      </div>
                      <span className="text-xs font-semibold text-gray-900">{review.cliente.nome}</span>
                      {review.verificado && (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-bold uppercase tracking-wider">
                          <Check className="w-3 h-3" /> Compra Verificada
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
